import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'dynamicVoronoi',
  name_en: 'Dynamic Voronoi',
  name_zh: '动态沃洛诺伊图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    data: amount => amount > 1,
    x: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    group: {
      itemType: [DEFINE.DATE],
      category: DEFINE.SINGLE
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    group: string;
  };
  animationDuration: number;
}

/**
 * 暂时不支持legend的点击功能
 */
export const dynamicVoronoi = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  withTimeline: true,
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util, hook } = api;
    const {
      dataIndex = 0,
      encoding: { x: nameAttribute, y: valueAttribute, group: groupAttribute },
      animationDuration: updateFrequency
    } = spec;
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);
    const presetOption = hook.buildStandardChartPresetOption(api);

    // * ------------------------- data
    const { dates, names } = util.dataEncoding.encoding(data.values.slice(), {
      dates: {
        field: groupAttribute,
        transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
      },
      names: { field: nameAttribute, transforms: [{ type: 'deDuplication' }] }
    });

    const colorList = presetOption?.color || [];
    const colorMapKeyByName = {} as { [k: string]: string };

    names.forEach((item, index) => {
      colorMapKeyByName[item] = colorList[index % colorList.length];
    });

    const dataset = [];

    dates.forEach(date => {
      const datasetItem = [];
      const dataWithSameDate = util.dataTransform.transform(data.values.slice(), [
        { type: 'filter', cb: item => item[groupAttribute] === date }
      ]);

      dataWithSameDate.forEach(item => {
        const nameIndex = names.findIndex(name => name === item[nameAttribute]);

        datasetItem.push({
          __origin: item,
          name: item[nameAttribute],
          ids: nameIndex,
          value: +item[valueAttribute],
          color: colorMapKeyByName[item[nameAttribute]]
        });
      });

      dataset.push(datasetItem);
    });

    // * ------------------------- chart
    const legend = {
      data: (names as string[]).map(item => ({
        name: item,
        color: colorMapKeyByName[item] || 'blue',
        type: util.const.SERIES_2_LEGEND_TYPE_MAP.voronoi,
        selected: true,
        disableSelect: true
      })),
      show: false,
      selected: (names as string[]).reduce((pre, cur) => {
        pre[cur] = true;
        return pre;
      }, {})
    };

    function getOption() {
      const chartViewRect = $dom.getBoundingClientRect();

      return {
        legend,
        animationDurationUpdate: updateFrequency,
        xAxis: [
          {
            dvNameLocation: null,
            show: false
          }
        ],
        yAxis: [
          {
            dvNameLocation: null,
            show: false
          }
        ],
        graphic: {
          elements: []
        },
        grid: {
          bottom: 0,
          right: 0,
          top: 0,
          left: 0
        },
        tooltip: {
          formatter: params_ => {
            const series = Array.isArray(params_) ? params_ : [params_];
            const items = [];

            series.forEach(item => {
              const result = hook.formatDataNumberValue(item.value, {
                type: 'table',
                unit: dataMetaInfo[item.seriesName]?.unit
              });

              items.push({
                type: util.const.SERIES_2_LEGEND_TYPE_MAP.voronoi,
                color: colorMapKeyByName[item.name],
                name: item.name,
                value: result.value + result.unit
              });
            });

            return hook.buildTooltip({
              config: hook.buildStandardChartPresetOption(api).tooltip,
              title: valueAttribute,
              items
            });
          }
        },
        series: [
          {
            type: 'custom',
            dvType: 'voronoi',
            dvRadius: (Math.min(chartViewRect.width, chartViewRect.height) / 2) * 0.9,
            universalTransition: {
              enabled: true
            }
          },
          {
            type: 'custom',
            dvType: 'voronoi',
            dvRadius: (Math.min(chartViewRect.width, chartViewRect.height) / 2) * 0.9,
            universalTransition: {
              enabled: false
            }
          }
        ]
      };
    }

    return renderApi => {
      const timelineIns = renderApi.renderTimelineProxy(dates as string[]);
      renderApi.renderLegendProxy(legend.data);

      // ! 依赖 DOM 尺寸，需要在图例和时间轴初始化后
      const option = renderApi.mergeChartOptionProxy(getOption());
      const renderParams = { option };
      const chartIns = renderApi.renderChartProxy(renderParams, { theme: 'pc-app-light' });

      const arg = {
        name: {
          show: true,
          fontSize: 10
        },
        value: {
          show: false,
          fontSize: 12
        },
        percent: {
          show: false,
          fontSize: 12
        }
      };
      const callback = function () {
        // console.log('-------');
      };
      const dataIndex = dates.length - 1;
      const helper = new ThsDataVStandardChart.helper.DynamicVoronoiHelper(
        chartIns,
        renderParams.option,
        dataset,
        arg,
        callback
      );
      helper.play(dataIndex);

      timelineIns?.on?.('change', e => {
        helper.play(e.index);
      });
    };
  }
});
